import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { TID } from '@/constants/testIds';
import { sounds } from '@/audio/sounds';
import H2HCard from '@/components/H2HCard';
import CompetitiveResultBlock from '@/components/CompetitiveResultBlock';
import { track, EVT } from '@/lib/telemetry';
import { setScreen } from '@/lib/sessionTracking';
import MatchResultPremium, { PremiumCta } from './MatchResultPremium';
import { hasPracticeTrace } from '@/lib/practiceLuckTrace';

const LOGO = '/kickard-shield.png';

/**
 * MatchSummaryView
 *
 * Phase 3.11 additions (Friend Match Rematch):
 *   • `matchType` — used to gate the direct Rematch button (friend only).
 *   • `rematch` — coordination state driven by the WS. Shape:
 *       {
 *         status: 'none' | 'requested' | 'accepted' | 'declined' | 'expired' | 'starting',
 *         isRequester: boolean,
 *         opponentIsRequester: boolean,
 *         expiresAt: ISO string | null,
 *         requestedByName: string | null,
 *       }
 *   • `onRematchRequest / onRematchAccept / onRematchDecline` — callbacks
 *     that dispatch the corresponding WS message.
 */
export default function MatchSummaryView({
  state,
  onRematch,
  onHome,
  isOnline = false,
  myPlayerId = 'p1',
  opponentName,
  h2h = null,
  h2hUpdating = false,
  myName = null,
  // Phase 3.11
  matchType = null,
  rematch = null,
  // PHASE 4.3.2B — parent decides Rematch-button visibility, honoring
  // the mode's `directRematchMaxIndex`. Friend has no cap; Quick caps
  // at 2 rematches after the original.
  canDirectRematch = true,
  onRematchRequest = null,
  onRematchAccept = null,
  onRematchDecline = null,
  // Phase 4.2.1 — Ranked Post-Match Competitive Result
  myGuestId = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const winner = state.endState?.winner;
  const reason = state.endState?.reason;
  const p1 = state.players.p1;
  const p2 = state.players.p2;
  const me = state.players[myPlayerId];
  const opp = state.players[myPlayerId === 'p1' ? 'p2' : 'p1'];
  const isWin = winner === myPlayerId;
  const isTie = winner === 'tie';
  const title = isTie ? 'TIE' : isWin ? 'VICTORY' : 'DEFEAT';

  // Sound sync: only fire victory/defeat once the summary screen is shown.
  useEffect(() => {
    if (isTie) return;
    if (isWin) sounds.victory();
    else sounds.defeat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 3.9A — funnel: summary viewed.
  useEffect(() => {
    setScreen('summary');
    track(EVT.SUMMARY_VIEWED, {
      matchType: isOnline ? matchType || undefined : 'bot_practice',
      metadata: { outcome: isTie ? 'tie' : isWin ? 'win' : 'loss', reason },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let subtitle;
  if (isTie) {
    subtitle = 'A close call';
  } else if (isOnline) {
    const oppLabel = opponentName || opp?.name || 'Opponent';
    if (reason === 'forfeit') {
      subtitle = isWin ? `${oppLabel} timed out` : `You lost by timeout`;
    } else if (reason === 'penalty') {
      subtitle = isWin ? `You beat ${oppLabel} on penalties` : `${oppLabel} won on penalties`;
    } else {
      subtitle = isWin ? `You defeated ${oppLabel}` : `${oppLabel} won`;
    }
  } else {
    subtitle = isWin ? 'You outplayed the bot' : 'The bot prevailed';
  }
  const color = isWin ? 'hsl(var(--k-gold-bright))' : isTie ? 'hsl(var(--k-muted))' : 'hsl(var(--k-red))';

  // PHASE 4.3.2B — Rematch controls now supported for Friend AND
  // Quick online matches. Quick has a server-enforced max-2 chain
  // cap; the parent passes `canDirectRematch=false` once reached so
  // the button hides gracefully and Queue Again remains available.
  const isRematchEligibleMode = isOnline && (matchType === 'friend' || matchType === 'quick_casual');
  const showRematchControls = isRematchEligibleMode && canDirectRematch !== false;

  // PHASE 5.0B — Match Result Premium (shared normal + penalty).
  // Render the premium Ranked Match Result for ALL finalized Ranked
  // outcomes — normal/penalty victory, normal/penalty defeat,
  // timeout/forfeit (either side), and draw if the current production
  // rules genuinely finalize one. Only non-Ranked matches
  // (Quick / Friend / Bot) fall through to the current production
  // MatchSummaryView body unchanged. Reuses existing data pipelines,
  // existing routes (`/ranked/leaderboard`, `/ranked/weekly-cup`,
  // `/ranked/search`), existing production rank crest mapping, and
  // existing Home Weekly trophy. No new backend endpoint, no new
  // matchmaking helper.
  const isRankedPremium =
    isOnline
    && matchType === 'ranked'
    && !!state.matchId
    && !!myGuestId;

  const handleOpenLuckReport = () => {
    try { track(EVT.SUMMARY_VIEWED, { metadata: { action: 'luck_report_open', matchId: state.matchId } }); } catch (_) { /* ignore */ }
    try {
      const snapshot = {
        state, myPlayerId, isOnline, opponentName,
        matchType, myName, myGuestId,
        h2h, h2hUpdating,
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(
        `kickard.summary.${state.matchId}`,
        JSON.stringify(snapshot),
      );
    } catch (_) { /* quota / private mode */ }
    navigate(
      `/match-report/${encodeURIComponent(state.matchId)}`,
      {
        state: {
          origin: 'match_summary',
          matchId: state.matchId,
          returnPath: location.pathname + location.search,
        },
      },
    );
  };

  // Unified premium Victory/Defeat for Quick / Friend / Bot (no tie in
  // Kickard; a tie keeps the existing fallback body below).
  const premiumMode = isRankedPremium ? 'ranked'
    : isTie ? null
    : !isOnline ? 'bot'
    : matchType === 'friend' ? 'friend'
    : matchType === 'quick_casual' ? 'quick'
    : null;

  if (premiumMode) {
    const premiumOutcome = isTie ? 'tie' : isWin ? 'win' : 'loss';
    let primaryAction = null;
    if (premiumMode === 'bot') {
      primaryAction = onRematch ? (
        <PremiumCta
          label="Rematch"
          testid={TID.summaryRematch}
          onClick={() => { track(EVT.QUEUE_AGAIN_CLICKED, { metadata: { context: 'bot_rematch' } }); onRematch(); }}
        />
      ) : null;
    } else if (premiumMode !== 'ranked' && showRematchControls) {
      primaryAction = (
        <RematchControls
          premium
          rematch={rematch}
          onRequest={onRematchRequest}
          onAccept={onRematchAccept}
          onDecline={onRematchDecline}
          opponentName={opponentName || opp?.name || 'Opponent'}
          matchType={matchType}
        />
      );
    }
    const showLuck = premiumMode === 'ranked'
      || (premiumMode === 'bot' ? hasPracticeTrace(state.matchId) : !!state.matchId);
    return (
      <div
        data-testid={TID.summaryScreen}
        className="min-h-[100dvh] w-full bg-black"
      >
        <MatchResultPremium
          state={state}
          matchId={state.matchId}
          myGuestId={myGuestId}
          myPlayerId={myPlayerId}
          myName={myName}
          opponentName={opponentName}
          h2h={h2h}
          h2hUpdating={h2hUpdating}
          isWin={isWin}
          isTie={isTie}
          reason={reason}
          mode={premiumMode}
          subtitle={subtitle}
          primaryAction={primaryAction}
          showLuckReport={showLuck}
          onViewLuckReport={handleOpenLuckReport}
          onHome={() => { track(EVT.RETURNED_HOME_AFTER_MATCH, { metadata: { outcome: premiumOutcome } }); onHome && onHome(); }}
        />
      </div>
    );
  }

  return (
    <div data-testid={TID.summaryScreen} className="kickard-frame stadium-bg flex flex-col items-center px-5 py-6">
      <motion.img
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        src={LOGO}
        alt="Kickard"
        style={{ width: 120, background: 'transparent' }}
      />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.18 }}
        className="font-heading mt-5 text-6xl tracking-[0.05em]"
        style={{ color, textShadow: `0 0 24px ${color}` }}
      >
        {title}
      </motion.div>
      <div className="font-body text-white/70 mt-1.5">{subtitle}</div>
      {reason === 'penalty' && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(var(--k-gold)/0.55)] bg-black/40">
          <span className="block w-1.5 h-1.5 rounded-full bg-[hsl(var(--k-gold-bright))]" style={{ boxShadow: '0 0 8px hsl(var(--k-gold-bright))' }} />
          <span className="font-heading text-[10px] tracking-[0.32em] text-[hsl(var(--k-gold-bright))] uppercase">Decided on Penalties</span>
        </div>
      )}

      {/* Score panel */}
      <div className="mt-8 w-full max-w-[340px] bg-[hsl(var(--k-bg-elev)/0.85)] border border-[hsl(var(--k-line))] rounded-xl p-5">
        <div className="grid grid-cols-3 gap-3 items-center">
          <div className="text-center">
            <div className="font-heading text-xs tracking-[0.2em] text-white/55">{p1.name}</div>
            <div className="font-heading text-5xl text-[hsl(var(--k-gold-bright))] leading-none mt-1">{p1.score}</div>
          </div>
          <div className="font-heading text-4xl text-white/25 text-center">·</div>
          <div className="text-center">
            <div className="font-heading text-xs tracking-[0.2em] text-white/55">{p2.name}</div>
            <div className="font-heading text-5xl text-[hsl(var(--k-red))] leading-none mt-1">{p2.score}</div>
          </div>
        </div>

        {reason === 'penalty' && state.penalty?.scores && (
          <div
            data-testid="summary-penalty-score"
            className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 items-center"
          >
            <div className="text-center">
              <div className="font-heading text-[10px] tracking-[0.3em] text-white/45">PENS</div>
              <div className="font-heading text-3xl text-[hsl(var(--k-gold))] leading-none mt-1">{state.penalty.scores.p1}</div>
            </div>
            <div className="font-heading text-2xl text-white/20 text-center">·</div>
            <div className="text-center">
              <div className="font-heading text-[10px] tracking-[0.3em] text-white/45">PENS</div>
              <div className="font-heading text-3xl text-[hsl(var(--k-red)/0.85)] leading-none mt-1">{state.penalty.scores.p2}</div>
            </div>
          </div>
        )}

        <div className="h-px bg-white/10 my-5" />

        <div className="grid grid-cols-2 gap-y-2.5 gap-x-5 text-sm font-body">
          <Stat label="Goals scored" value={me.score} />
          <Stat label="Goals conceded" value={opp.score} />
          <Stat label="Attacks performed" value={me.attacksPerformed} />
          <Stat label="Saves performed" value={me.savesPerformed} />
        </div>
      </div>

      {/* Phase 4.2.1 — Ranked-only Competitive Result (SR + Weekly Cup) */}
      {isOnline && matchType === 'ranked' && state.matchId && (
        <CompetitiveResultBlock
          matchId={state.matchId}
          matchType={matchType}
          myGuestId={myGuestId}
        />
      )}

      {isOnline && (h2h || h2hUpdating) && (
        <div data-testid="h2h-summary-card" className="mt-5 w-full max-w-[340px]">
          {h2h && (
            <H2HCard
              h2h={h2h}
              myName={myName || me.name}
              opponentName={opponentName || opp.name}
            />
          )}
          {h2hUpdating && (
            <div
              data-testid="h2h-updating"
              className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(var(--k-gold)/0.35)] bg-black/40"
            >
              <span className="block w-1.5 h-1.5 rounded-full bg-[hsl(var(--k-gold-bright))] animate-pulse" />
              <span className="font-heading text-[10px] tracking-[0.28em] text-white/70 uppercase">
                Updating rivalry record…
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex-1" />

      {isOnline && state.matchId && (
        <div className="w-full max-w-[340px] mt-6">
          <button
            data-testid="summary-luck-report-btn"
            className="w-full py-2.5 rounded-md border border-[hsl(var(--k-gold)/0.55)] bg-[hsl(var(--k-gold)/0.08)] hover:bg-[hsl(var(--k-gold)/0.15)] transition-colors font-heading text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--k-gold-bright))]"
            onClick={() => {
              try { track(EVT.SUMMARY_VIEWED, { metadata: { action: 'luck_report_open', matchId: state.matchId } }); } catch (_) { /* ignore */ }
              // PHASE 5.0A PASS 3 — explicit origin so the Luck Report
              // Back button returns to THIS summary (not the old Online
              // lobby via browser-history depth).
              // PHASE 5.0A PASS 3A — Snapshot the finalized summary
              // to sessionStorage so Back from Luck Report can restore
              // it via the dedicated `/match-summary/:matchId` route,
              // even after the WebSocket / live room has closed.
              // This is a READ-ONLY view of an ALREADY finalized match:
              // no re-finalization, no duplicate SR, no duplicate Weekly
              // Cup — the target route only renders, never re-emits.
              try {
                const snapshot = {
                  state, myPlayerId, isOnline, opponentName,
                  matchType, myName, myGuestId,
                  h2h, h2hUpdating,
                  // Rematch controls are intentionally NOT re-armed on
                  // the restored view — the room may have released; the
                  // restored summary is a review, and Home / Play Hub /
                  // Luck Report are the safe onward actions.
                  savedAt: new Date().toISOString(),
                };
                sessionStorage.setItem(
                  `kickard.summary.${state.matchId}`,
                  JSON.stringify(snapshot),
                );
              } catch (_) { /* quota / private mode → fall back to /online */ }
              navigate(
                `/match-report/${encodeURIComponent(state.matchId)}`,
                {
                  state: {
                    origin: 'match_summary',
                    matchId: state.matchId,
                    returnPath: location.pathname + location.search,
                  },
                },
              );
            }}
          >
            View Luck Report →
          </button>
          <div className="mt-1.5 text-center text-white/40 text-[10px]">
            See your Average Match Card and every card you received.
          </div>
        </div>
      )}

      <div className="w-full max-w-[340px] flex flex-col gap-3 mt-8">
        {!isOnline && (
          <button
            data-testid={TID.summaryRematch}
            className="btn-primary"
            onClick={() => { track(EVT.QUEUE_AGAIN_CLICKED, { metadata: { context: 'bot_rematch' } }); onRematch && onRematch(); }}
          >
            Rematch
          </button>
        )}

        {showRematchControls && (
          <RematchControls
            rematch={rematch}
            onRequest={onRematchRequest}
            onAccept={onRematchAccept}
            onDecline={onRematchDecline}
            opponentName={opponentName || opp?.name || 'Opponent'}
            matchType={matchType}
          />
        )}

        <button
          data-testid={TID.summaryHome}
          className="btn-secondary"
          onClick={() => { track(EVT.RETURNED_HOME_AFTER_MATCH, { metadata: { outcome: isTie ? 'tie' : isWin ? 'win' : 'loss' } }); onHome && onHome(); }}
        >
          Home
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex justify-between border-b border-white/5 pb-1">
      <span className="text-white/55">{label}</span>
      <span className="text-white font-mono">{value}</span>
    </div>
  );
}

/**
 * RematchControls — presentational state machine for the Rematch button set.
 * All state comes from the parent; this is stateless except for a countdown
 * re-render tick.
 */
function RematchControls({ rematch, onRequest, onAccept, onDecline, opponentName, premium = false }) {
  const status = rematch?.status || 'none';
  const isRequester = !!rematch?.isRequester;
  const opponentIsRequester = !!rematch?.opponentIsRequester;
  const expiresAt = rematch?.expiresAt || null;

  // Re-render every 500ms while a countdown is active.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (status !== 'requested') return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [status]);

  const secondsLeft = useMemo(() => {
    if (!expiresAt) return null;
    try {
      const diff = Math.max(0, new Date(expiresAt).getTime() - Date.now());
      return Math.ceil(diff / 1000);
    } catch { return null; }
  }, [expiresAt, status, tick]);

  // Case: idle — show "Rematch" button.
  if (status === 'none' || status === 'started') {
    if (premium) {
      return (
        <PremiumCta
          label="Rematch"
          testid="summary-rematch-btn"
          onClick={() => { track(EVT.REMATCH_REQUEST_CLICKED, { metadata: { context: 'friend_match' } }); onRequest && onRequest(); }}
        />
      );
    }
    return (
      <button
        data-testid="summary-rematch-btn"
        className="btn-primary"
        onClick={() => { track(EVT.REMATCH_REQUEST_CLICKED, { metadata: { context: 'friend_match' } }); onRequest && onRequest(); }}
      >
        Rematch
      </button>
    );
  }

  // Case: I requested, waiting for opponent.
  if (status === 'requested' && isRequester) {
    return (
      <div className="flex flex-col gap-2" data-testid="rematch-waiting">
        <div className="w-full py-2 px-3 rounded-md bg-white/[0.05] border border-white/10 text-center">
          <span className="font-heading text-[10px] tracking-[0.28em] uppercase text-white/70">
            Waiting for {opponentName}…
          </span>
          {secondsLeft !== null && (
            <span className="ml-2 font-mono text-white/50 text-xs" data-testid="rematch-countdown">
              {secondsLeft}s
            </span>
          )}
        </div>
      </div>
    );
  }

  // Case: opponent requested — show Accept / Decline.
  if (status === 'requested' && opponentIsRequester) {
    return (
      <div className="flex flex-col gap-2" data-testid="rematch-invite">
        <div className="text-center text-white/70 text-xs">
          <span data-testid="rematch-invite-text">{opponentName} wants a rematch</span>
          {secondsLeft !== null && (
            <span className="ml-2 font-mono text-white/50" data-testid="rematch-countdown">
              {secondsLeft}s
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="summary-rematch-accept-btn"
            className="btn-primary"
            onClick={() => { track(EVT.REMATCH_ACCEPT_CLICKED, { metadata: { context: 'friend_match' } }); onAccept && onAccept(); }}
          >
            Accept
          </button>
          <button
            data-testid="summary-rematch-decline-btn"
            className="btn-secondary"
            onClick={() => { track(EVT.REMATCH_DECLINE_CLICKED, { metadata: { context: 'friend_match' } }); onDecline && onDecline(); }}
          >
            Decline
          </button>
        </div>
      </div>
    );
  }

  // Case: both accepted, starting.
  if (status === 'starting' || status === 'accepted') {
    return (
      <div className="w-full py-2 px-3 rounded-md bg-[hsl(var(--k-gold)/0.15)] border border-[hsl(var(--k-gold)/0.35)] text-center" data-testid="rematch-starting">
        <span className="font-heading text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--k-gold-bright))]">
          Starting rematch…
        </span>
      </div>
    );
  }

  // Case: declined.
  if (status === 'declined') {
    return (
      <div className="flex flex-col gap-2" data-testid="rematch-declined">
        <div className="w-full py-2 px-3 rounded-md bg-white/[0.05] border border-white/10 text-center">
          <span className="font-heading text-[10px] tracking-[0.28em] uppercase text-white/60">
            Rematch declined
          </span>
        </div>
        <button
          data-testid="summary-rematch-btn"
          className="btn-secondary"
          onClick={() => { track(EVT.REMATCH_REQUEST_CLICKED, { metadata: { context: 'friend_match', retry: true } }); onRequest && onRequest(); }}
        >
          Ask again
        </button>
      </div>
    );
  }

  // Case: expired.
  if (status === 'expired') {
    return (
      <div className="flex flex-col gap-2" data-testid="rematch-expired">
        <div className="w-full py-2 px-3 rounded-md bg-white/[0.05] border border-white/10 text-center">
          <span className="font-heading text-[10px] tracking-[0.28em] uppercase text-white/60">
            Rematch expired
          </span>
        </div>
        <button
          data-testid="summary-rematch-btn"
          className="btn-secondary"
          onClick={() => { track(EVT.REMATCH_REQUEST_CLICKED, { metadata: { context: 'friend_match', retry: true } }); onRequest && onRequest(); }}
        >
          Ask again
        </button>
      </div>
    );
  }

  return null;
}
